import { msalInstance, msalInitializedPromise } from './msal-instance';
import {
  PARTNER_API_URL,
  ADMIN_API_URL,
  API_TOKEN,
  VITE_AZURE_CLIENTID,
  DEPLOYED_APP_URL,
  COUNTRY_CODE,
} from './sys';

export {
  PARTNER_API_URL,
  ADMIN_API_URL,
  API_TOKEN,
  VITE_AZURE_CLIENTID,
  DEPLOYED_APP_URL,
  COUNTRY_CODE,
};
export { VITE_AZURE_AUTHORITY } from './sys';

const CWID_CLAIM = 'https://bayer.com/cwid';

export const API_ENDPOINTS = {
  license_status: (state, pan) =>
    `/distributors?tax_number=${pan}&state_code=${state}`,
  license_submission: (sapids) =>
    `/distributors/license-documents?sap_ids=${sapids}`,
};

const partnerHeaders = () => ({
  'Content-Type': 'application/json',
  ...(API_TOKEN ? { 'x-api-key': API_TOKEN } : {}),
});

const handleAdminLogout = async () => {
  try {
    await msalInstance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    });
  } catch (error) {
    console.error('Logout failed:', error);
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = '/login';
  }
};

const getCwidFromAccount = (account) => {
  const cwid = account?.idTokenClaims?.[CWID_CLAIM];
  return typeof cwid === 'string' && cwid.length > 0 ? cwid : undefined;
};

const buildAdminHeaders = async () => {
  await msalInitializedPromise;
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    await handleAdminLogout();
    throw new Error('No authenticated user');
  }

  const account = accounts[0];
  let accessToken;
  try {
    const tokenResponse = await msalInstance.acquireTokenSilent({
      account,
      scopes: [`${VITE_AZURE_CLIENTID}/.default`],
    });
    accessToken = tokenResponse.accessToken;
  } catch (silentError) {
    console.warn('Silent token acquisition failed:', silentError);
    await handleAdminLogout();
    throw new Error('Authentication failed');
  }

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    countryCode: COUNTRY_CODE,
  };

  const userId = getCwidFromAccount(account);
  if (userId) {
    headers.userId = userId;
  }

  return headers;
};

const parseGetResponse = async (response) => {
  if (response.status === 204) {
    return null;
  }
  if (response.status === 200) {
    const contentType = response.headers.get('content-type');
    if (contentType && !contentType.includes('application/json')) {
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      const contentDisposition = response.headers.get('Content-Disposition');
      let fileName = 'download.csv';
      if (contentDisposition && contentDisposition.includes('filename=')) {
        fileName = contentDisposition
          .split('filename=')[1]
          .split(';')[0]
          .trim()
          .replace(/"/g, '');
      }
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
      return;
    }
    return response.json();
  }
  const errorText = await response.text();
  throw new Error(`HTTP error ${response.status}: ${errorText}`);
};

const buildUrl = (baseUrl, endpoint, params) => {
  const url = new URL(`${baseUrl}${endpoint}`);
  if (params) {
    Object.entries(params).forEach(([key, value]) =>
      url.searchParams.append(key, value),
    );
  }
  return url;
};

/** Public / Partner GW GET */
export const partnerGet = async (endpoint, params) => {
  const url = buildUrl(PARTNER_API_URL, endpoint, params);
  const response = await fetch(url, {
    method: 'GET',
    headers: partnerHeaders(),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP error ${response.status}: ${errorText}`);
  }
  return parseGetResponse(response);
};

/** Public / Partner GW POST */
export const partnerPost = async (endpoint, data) => {
  const response = await fetch(`${PARTNER_API_URL}${endpoint}`, {
    method: 'POST',
    headers: partnerHeaders(),
    body: JSON.stringify(data),
  });

  const responseData = await response.json();

  if (response.ok) {
    return responseData;
  }
  if (response.status === 409) {
    return { ...responseData, status: response.status };
  }
  window.alert('Something went Wrong!');
  return;
};

/** Admin GW GET — Azure Bearer + userId */
export const adminGet = async (endpoint, params) => {
  const headers = await buildAdminHeaders();
  const url = buildUrl(ADMIN_API_URL, endpoint, params);
  const response = await fetch(url, {
    method: 'GET',
    headers,
  });

  if (response.status === 401 || response.status === 403) {
    await handleAdminLogout();
    throw new Error('Session expired or access denied');
  }
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP error ${response.status}: ${errorText}`);
  }
  return parseGetResponse(response);
};

/** Admin GW PUT — Azure Bearer + userId */
export const adminPut = async (endpoint, data) => {
  const headers = await buildAdminHeaders();
  const response = await fetch(`${ADMIN_API_URL}${endpoint}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(data),
  });

  if (response.status === 401 || response.status === 403) {
    await handleAdminLogout();
    throw new Error('Session expired or access denied');
  }

  const responseData = await response.json();
  if (response.ok) {
    return responseData;
  }
  if (response.status === 409) {
    return { ...responseData, status: response.status };
  }
  window.alert('Something went Wrong!');
  return;
};

/** @deprecated Use partnerGet — kept for public call sites */
export const get = partnerGet;
/** @deprecated Use partnerPost — kept for public call sites */
export const post = partnerPost;
/** @deprecated Use adminPut for review dashboard */
export const put = adminPut;
